/**
 * The three providers everything under the shell needs: React Query, the
 * `ApiClient` the queries run through, and the signed-in state they are gated
 * on.
 *
 * Exported as one component so `main.tsx` and every test mount the same tree.
 * A test that assembled its own set would be free to configure them
 * differently from production and prove less than it appears to.
 *
 * `session` is a required prop rather than a `useSession()` call inside this
 * component: see `./authState`. `./AuthBridge` is the one production module
 * that reads the auth contract and fills it in.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query';

import type { ApiClient } from '../../lib/api';
import { ApiFailureError } from '../../lib/http/errors';
import { AuthStateContext, SignOutContext, useAuthState, type SessionState } from './authState';
import { ApiClientContext } from './client';
import { ME_QUERY_KEY } from './session';

/**
 * A 403 from a tenant- or campaign-scoped call is how a membership revoked
 * mid-session reaches the client; asking `/control/me` again lets the
 * context resolve to "not yours" (platform design §17.8). Excludes
 * `/control/me`'s own query, whose failure this would otherwise chase
 * forever.
 */
function refreshSessionOnForbidden(
  client: QueryClient,
  error: unknown,
  queryKey: readonly unknown[] | null,
): void {
  if (!(error instanceof ApiFailureError) || error.failure.kind !== 'forbidden') return;
  if (
    queryKey !== null &&
    queryKey.length === ME_QUERY_KEY.length &&
    queryKey.every((part, i) => part === ME_QUERY_KEY[i])
  ) {
    return;
  }
  void client.invalidateQueries({ queryKey: ME_QUERY_KEY, exact: true });
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => refreshSessionOnForbidden(client, error, query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (error) => refreshSessionOnForbidden(client, error, null),
    }),
    defaultOptions: {
      queries: {
        // A read model is a dated sample, never a live feed (platform design
        // §2). Refetching because a window regained focus implies a freshness
        // the data does not have, so it is off.
        refetchOnWindowFocus: false,
        // TanStack's default backoff (1s + 2s + 4s) sits between a real
        // failure and the screen's ErrorState; nothing here calls a flaky
        // transport worth retrying.
        retry: false,
      },
    },
  });
  return client;
}

/**
 * Empties the cache when the identity behind it changes, so `/control/me` is
 * asked again and nothing the previous identity read is served to the next.
 *
 * Without this, the answer given for one identity outlives it. Signing in after
 * a signed-out first paint serves the cached `unauthenticated` refusal for as
 * long as the tab is open -- nothing refetches it, because `retry` and
 * `refetchOnWindowFocus` are both off and no component remounts. Signing out is
 * the same fault pointed the other way, and worse: the previous user's
 * capabilities stay in the cache and keep rendering their navigation.
 *
 * Unscoped, because `/control/me` is not the only answer that belongs to one
 * identity: the operator register, a tenant's movements and every other
 * scoped read are cached under keys that name the resource and not who asked
 * for it, so a key-by-key list of what to drop is a list that goes stale on
 * the next query added. `useOpsEntitlement` is the sharpest case -- it reads
 * `GET /ops/tenants` out of the cache, so a second operator signing in
 * behind the first is entitled by the first one's answer.
 *
 * Rendered once, above the consumers, rather than folded into `useMe()`: four
 * components call that hook on a single page, and four copies of this effect
 * would each reset the query the other three are reading.
 */
function IdentityReset(): null {
  const queryClient = useQueryClient();
  const uid = useAuthState().user?.uid ?? null;
  // Seeded with the mount-time identity so the first run is not a reset. There
  // is nothing cached to reset at mount, and resetting anyway would cancel the
  // fetch the consumers below are about to start.
  const seen = useRef<string | null>(uid);

  useEffect(() => {
    if (seen.current === uid) return;
    seen.current = uid;
    // `resetQueries`, not `invalidateQueries`: invalidation does not make an
    // errored query fresh, and the refusal cached from the signed-out moment is
    // exactly the state that has to go. Every key, not only `/control/me`'s: a
    // data entry survives the sign-out otherwise and is what the next user reads
    // first.
    void queryClient.resetQueries();
  }, [uid, queryClient]);

  return null;
}

export function AppProviders(props: {
  client: ApiClient;
  session: SessionState;
  // Optional: most tests under this tree never click sign-out, and giving
  // every one of them a fake `Session` just to satisfy this prop would be
  // the collision `AuthBridge`'s doc comment already argues against.
  signOut?: () => Promise<void>;
  queryClient?: QueryClient;
  children: ReactNode;
}): ReactNode {
  // Held in state rather than built inline: a client rebuilt on every render
  // would empty the cache under `IdentityReset` and every consumer on each pass.
  const [fallbackClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={props.queryClient ?? fallbackClient}>
      <ApiClientContext value={props.client}>
        <AuthStateContext value={props.session}>
          <SignOutContext value={props.signOut ?? (() => Promise.resolve())}>
            <IdentityReset />
            {props.children}
          </SignOutContext>
        </AuthStateContext>
      </ApiClientContext>
    </QueryClientProvider>
  );
}
